import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Trophy,
  Building2,
  Shield,
  ShieldCheck,
  LayoutDashboard,
  KeyRound,
  LogOut,
  Menu,
  Megaphone,
  Moon,
  Sun,
  Smartphone,
  Ticket,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/auth/AuthContext';
import { setLanguage, SUPPORTED_LANGUAGES, type Language } from '@/i18n';
import { useDataScope, type DataScope } from '@/lib/dataPurpose';
import { safePersonReturn } from '@/lib/personDetailLink';

import { Button, IconButton, cx } from './ui';

const THEME_KEY = 'evalyte.admin.theme';
const SCOPED_PATHS = new Set(['/', '/people', '/clubs', '/teams', '/leaderboards', '/licenses', '/finance']);

const NAV = [
  { to: '/', key: 'overview', icon: LayoutDashboard, end: true, group: 'daily' },
  { to: '/people', key: 'people', icon: Users, end: false, group: 'daily' },
  { to: '/clubs', key: 'clubs', icon: Building2, end: false, group: 'daily' },
  { to: '/teams', key: 'teams', icon: Shield, end: false, group: 'daily' },
  { to: '/leaderboards', key: 'leaderboards', icon: Trophy, end: false, group: 'daily' },
  { to: '/licenses', key: 'licenses', icon: KeyRound, end: false, group: 'management' },
  { to: '/finance', key: 'finance', icon: Wallet, end: false, group: 'management' },
  { to: '/vip-codes', key: 'vip', icon: Ticket, end: false, group: 'management' },
  { to: '/marketing-consents', key: 'marketing', icon: ShieldCheck, end: false, group: 'management' },
  { to: '/community', key: 'community', icon: Megaphone, end: false, group: 'management' },
  { to: '/ios-devices', key: 'iosDevices', icon: Smartphone, end: false, group: 'system' },
  { to: '/administrators', key: 'administrators', icon: Users, end: false, group: 'system' },
  { to: '/security', key: 'security', icon: ShieldCheck, end: false, group: 'system' },
  { to: '/scoring', key: 'scoring', icon: BarChart3, end: false, group: 'system' },
] as const;

export function AppShell() {
  const { t, i18n } = useTranslation();
  const { account, signOut } = useAuth();
  const location = useLocation();
  const personDetail = /^\/people\/[^/]+$/.test(location.pathname);
  const scopedPage = SCOPED_PATHS.has(location.pathname) || location.pathname.startsWith('/clubs/') || personDetail;
  const navigate = useNavigate();
  const { scope, setScope } = useDataScope();
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoutError, setLogoutError] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [dark, setDark] = useState(() =>
    document.documentElement.classList.contains('dark'),
  );

  // A sidebar that stays open over the page you just navigated to is the
  // classic mobile-nav bug.
  useEffect(() => setMenuOpen(false), [location.pathname]);



  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    try {
      localStorage.setItem(THEME_KEY, dark ? 'dark' : 'light');
    } catch {
      /* The choice still holds for this session. */
    }
  }, [dark]);

  function changeScope(next: DataScope) {
    if (next === scope) return;
    setScope(next);
    if (!scopedPage) return;
    const detailReturn = personDetail ? safePersonReturn(new URLSearchParams(location.search).get('return_to')) : null;
    const path = personDetail ? (detailReturn?.split('?')[0].startsWith('/clubs/') ? '/clubs' : detailReturn?.split('?')[0] ?? '/people') : location.pathname.startsWith('/clubs/') ? '/clubs' : location.pathname;
    const params = new URLSearchParams(personDetail && path === '/clubs' ? '' : personDetail ? detailReturn?.split('?')[1] ?? '' : path === location.pathname ? location.search : '');
    params.delete('page');
    params.delete('activity_page');
    // A team belongs to one data scope. Keep unrelated leaderboard filters,
    // but do not carry a demo team into the operations view (or vice versa).
    if (path === '/leaderboards') params.delete('team_id');
    params.delete('person_id');
    params.delete('return_to');
    params.delete('club_name');
    navigate(`${path}${params.size ? `?${params}` : ''}`, { replace: true });
  }

  return (
    <div className="flex min-h-dvh flex-col lg:flex-row">
      {/* Skip link: the first stop for a keyboard user, past seven nav items. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-chrome focus:px-3 focus:py-2 focus:text-sm focus:text-on-chrome"
      >
        {t('app.skipToContent')}
      </a>

      <header className="flex items-center gap-2 border-b border-line bg-chrome px-4 py-2.5 text-on-chrome lg:hidden">
        <IconButton
          variant="ghost"
          label={t(menuOpen ? 'app.closeMenu' : 'app.openMenu')}
          aria-expanded={menuOpen}
          icon={menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          onClick={() => setMenuOpen((open) => !open)}
          className="text-on-chrome hover:bg-white/10"
        />
        <span className="text-sm font-semibold">{t('app.name')}</span>
      </header>

      <nav
        aria-label={t('app.name')}
        className={cx(
          'flex shrink-0 flex-col gap-1 bg-chrome px-3 py-4 text-on-chrome lg:w-60',
          menuOpen ? 'block' : 'hidden lg:flex',
        )}
      >
        <div className="hidden items-center gap-2 px-2 pb-4 lg:flex">
          <span aria-hidden className="h-2 w-2 rounded-full bg-accent" />
          <span className="text-sm font-semibold tracking-tight">{t('app.name')}</span>
        </div>

        {NAV.map(({ to, key, icon: Icon, end, group }, index) => (
          <div key={to}>
            {(index === 0 || NAV[index - 1].group !== group) ? <p className="mb-1 mt-3 px-3 text-2xs font-semibold uppercase tracking-wide text-white/50">{t(`navGroup.${group}`)}</p> : null}
          <NavLink
            to={to}
            end={end}
            className={({ isActive }) =>
              cx(
                'flex min-h-[2.5rem] items-center gap-3 rounded-lg px-3 text-sm transition-colors',
                isActive
                  ? 'bg-white/12 font-medium text-on-chrome'
                  : 'text-white/70 hover:bg-white/10 hover:text-on-chrome',
              )
            }
          >
            {({ isActive }) => (
              <>
                {/* The active marker is a bar as well as a colour change, so
                    it survives being read without colour. */}
                <span
                  aria-hidden
                  className={cx(
                    'h-4 w-0.5 rounded-full',
                    isActive ? 'bg-accent' : 'bg-transparent',
                  )}
                />
                <Icon aria-hidden className="h-4 w-4 shrink-0" />
                {t(`nav.${key}`)}
              </>
            )}
          </NavLink>
          </div>
        ))}

        <div className="mt-auto space-y-2 border-t border-white/10 pt-3">
          <div className="flex items-center gap-1 px-1">
            {SUPPORTED_LANGUAGES.map((language) => (
              <button
                key={language}
                type="button"
                onClick={() => setLanguage(language as Language)}
                aria-pressed={i18n.language === language}
                className={cx(
                  'min-h-[2rem] flex-1 rounded-md px-2 text-xs font-medium uppercase transition-colors',
                  i18n.language === language
                    ? 'bg-white/15 text-on-chrome'
                    : 'text-white/60 hover:bg-white/10',
                )}
              >
                {language}
              </button>
            ))}
            {/*
              Pressed rather than a label that changes with the state, so it
              reads the same way as the two language buttons beside it: the
              label names the setting, the state says whether it is on.
            */}
            <IconButton
              variant="ghost"
              label={t('app.theme')}
              aria-pressed={dark}
              icon={dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              onClick={() => setDark((value) => !value)}
              className="text-white/70 hover:bg-white/10 hover:text-on-chrome"
            />
          </div>

          <div className="px-2">
            <p className="truncate text-2xs text-white/50" title={account?.email}>
              {account?.email}
            </p>
          </div>
          <Button
            variant="ghost"
            icon={<LogOut className="h-4 w-4" />}
            onClick={() => { setLoggingOut(true); setLogoutError(false); void signOut().catch(() => setLogoutError(true)).finally(() => setLoggingOut(false)); }}
            loading={loggingOut}
            className="w-full justify-start text-white/70 hover:bg-white/10 hover:text-on-chrome"
          >
            {t('app.signOut')}
          </Button>
          {logoutError && <p role="alert" className="px-2 text-xs text-rose-200">{t('adminAuth.logoutFailed')}</p>}
        </div>
      </nav>

      <main id="main" className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
        {location.pathname !== '/scoring' && location.pathname !== '/security' && location.pathname !== '/administrators' ? (
          <div className="mb-5 flex flex-wrap items-center gap-3 rounded-lg border border-line bg-surface-raised px-4 py-3">
            <label htmlFor="admin-data-scope" className="text-sm font-medium text-ink">{t('dataPurpose.scope')}</label>
            <select id="admin-data-scope" value={scope} onChange={(event) => changeScope(event.target.value as DataScope)}
              className="min-h-9 rounded-md border border-line bg-surface px-3 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand">
              {(['production', 'demo', 'store_review', 'internal_test', 'all'] as const).map((value) =>
                <option key={value} value={value}>{t(`dataPurpose.${value}`)}</option>)}
            </select>
            <span className="text-xs text-ink-subtle">{t(scope === 'all' ? 'dataPurpose.allHint' : scope === 'production' ? 'dataPurpose.productionHint' : 'dataPurpose.nonProductionHint')}</span>
          </div>
        ) : null}
        <Outlet key={scopedPage ? scope : 'unscoped'} />
      </main>
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="break-words text-xl font-semibold tracking-tight text-ink [overflow-wrap:anywhere]">{title}</h1>
        {subtitle ? (
          <p className="mt-1 max-w-2xl text-sm text-ink-subtle">{subtitle}</p>
        ) : null}
      </div>
      {actions ? <div className="flex gap-2">{actions}</div> : null}
    </div>
  );
}
